import { useCallback, useEffect, useState } from "react";

import { Filter, SortAscending } from "@carbon/icons-react";
import { unwrapResult } from "@reduxjs/toolkit";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation, useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { getMessage } from "@/utils/message";

import { Loader2 } from "lucide-react";
import {
  getGroupInvitationForUser,
  getGroupInvitationRootForUser,
} from "../../api/group.api";
import SearchModal from "../../components/SearchModal/SearchModal";
import {
  GroupSortDateEnum,
  GroupSortEnum,
} from "../../constants/group.constant";
import {
  getGroupInvitationForUserThunk,
  updateInvitationRequests,
} from "../../group";
import { IGroupInvitation } from "../../types/group.type";
import GroupCardInvite from "./component/GroupCardInvite";

const GroupInviteScreen = () => {
  const { invitationRequests } = useAppSelector((state) => state.group);
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const [rootGroups, setRootGroups] = useState<IGroupInvitation[]>([]);
  const [page, setPage] = useState(1);
  const [isLoadMore, setIsLoadMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalElement, setTotalElement] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const [rootGroupId, setRootGroupId] = useState<number | undefined>(undefined);
  const [sort, setSort] = useState<string | null>(null);

  // Update filter states when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    setRootGroupId(Number(urlParams.get("rootGroupId")) || undefined);
    setSort(urlParams.get("sort"));
  }, [location.search]);

  let isFiltering = false;
  if (rootGroupId || sort) {
    isFiltering = true;
  }

  const fetchMyGroupsInfiniteScroll = async (pageScroll: number) => {
    try {
      setIsLoadMore(true);
      const result = await getGroupInvitationForUser({
        pageNumber: pageScroll,
        pageSize: 9,
        rootGroupId: rootGroupId,
        sort: sort as GroupSortEnum,
      });
      if (!result.data.length || result.data.length < 9) {
        setIsLoadMore(false);
      }
      dispatch(
        updateInvitationRequests([...invitationRequests, ...result.data])
      );
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage(error.response?.data.errors[0]),
      });
    }
  };

  const fetchMyGroups = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getGroupInvitationForUser({
        rootGroupId: rootGroupId,
      });
      setTotalElement(result.totalElements);
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage("MSG12"),
      });
    } finally {
      setLoading(false);
    }
  }, [rootGroupId, toast]);

  const fetchInvites = useCallback(
    (pageNumber: number) => {
      dispatch(
        getGroupInvitationForUserThunk({
          pageNumber,
          pageSize: 9,
          rootGroupId,
          sort: sort as GroupSortEnum,
        })
      )
        .then(unwrapResult)
        .then((result) => {
          if (!result.length || result.length < 9) {
            setIsLoadMore(false);
          }
        });
    },
    [rootGroupId, sort, dispatch]
  );

  const fetchRootGroups = useCallback(async () => {
    try {
      const result = await getGroupInvitationRootForUser({});
      setRootGroups(result.data);
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage("MSG12"),
      });
    }
  }, [toast]);

  useEffect(() => {
    setPage(1);
    fetchMyGroups();
    fetchRootGroups();
    fetchInvites(page);
  }, [rootGroupId, sort, fetchInvites, fetchMyGroups, fetchRootGroups]);

  return (
    <div className="min-h-screen bg-secondary-25">
      {/* Enhanced Header with Integrated Filters */}
      <div className="bg-white sticky top-[70px] z-40">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Header Row */}
          <div className="py-4 border-b border-secondary-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">
                    Lời mời tham gia
                  </h1>
                  <p className="text-sm text-secondary-600 mt-1">
                    Quản lý các lời mời tham gia nhóm của bạn
                  </p>
                </div>
                <Badge
                  variant="secondary"
                  className="bg-primary-50 text-primary-700 px-2 py-1 text-xs font-semibold"
                >
                  {totalElement}
                </Badge>
              </div>
              <div className="flex items-center space-x-3">
                <SearchModal />
              </div>
            </div>
          </div>

          {/* Quick Filters Row */}
          <div className="py-3">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              {/* Quick Filter Pills */}
              <div className="flex items-center gap-3">
                <div className="flex items-center space-x-2">
                  <Filter
                    size={16}
                    className="text-secondary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700">
                    Lọc nhanh:
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/* All Invitations Pill */}
                  <button
                    onClick={() => {
                      setRootGroupId(undefined);
                      setSort(null);
                      setPage(1);
                      navigate(`${window.location.pathname}`);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                      !isFiltering
                        ? "bg-primary-500 text-white border-primary-500 shadow-sm"
                        : "bg-white text-secondary-600 border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50"
                    }`}
                  >
                    Tất cả lời mời
                  </button>
                </div>
              </div>

              {/* Advanced Filters */}
              <div className="flex items-center gap-2">
                {/* Sort Dropdown */}
                <Select
                  value={sort || GroupSortDateEnum.NEWEST}
                  onValueChange={(value) => {
                    const urlParams = new URLSearchParams(location.search);
                    if (value === GroupSortDateEnum.NEWEST) {
                      setSort(null);
                      urlParams.delete("sort");
                    } else {
                      urlParams.set("sort", value);
                      setPage(1);
                      setSort(value);
                    }
                    navigate(
                      `${window.location.pathname}?${urlParams.toString()}`
                    );
                  }}
                >
                  <SelectTrigger className="w-auto min-w-[120px] h-8 text-xs border-secondary-200 focus:border-primary-300 hover:border-primary-200 transition-colors bg-white">
                    <SortAscending
                      size={14}
                      className="mr-1 text-secondary-500"
                    />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={GroupSortDateEnum.NEWEST}>
                        Mới nhất
                      </SelectItem>
                      <SelectItem value={GroupSortDateEnum.OLDEST}>
                        Cũ nhất
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>

                {/* Organization Filter (if has root groups) */}
                {rootGroups.length > 0 && (
                  <Select
                    value={rootGroupId ? String(rootGroupId) : "default"}
                    onValueChange={(value) => {
                      const urlParams = new URLSearchParams(location.search);
                      if (value === "default") {
                        setRootGroupId(undefined);
                        urlParams.delete("rootGroupId");
                      } else {
                        urlParams.set("rootGroupId", value);
                        setRootGroupId(Number(value));
                        setPage(1);
                      }
                      navigate(
                        `${window.location.pathname}?${urlParams.toString()}`
                      );
                    }}
                  >
                    <SelectTrigger className="w-auto min-w-[130px] max-w-[160px] h-8 text-xs border-secondary-200 focus:border-primary-300 hover:border-primary-200 transition-colors bg-white">
                      <SelectValue placeholder="Chọn tổ chức" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="default">Tất cả tổ chức</SelectItem>
                        {rootGroups.map((group) => (
                          <SelectItem
                            key={group.id}
                            value={String(group.id)}
                          >
                            <span className="truncate">{group.name}</span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}

                {/* Clear Filters */}
                {isFiltering && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRootGroupId(undefined);
                      setSort(null);
                      setPage(1);
                      navigate(`${window.location.pathname}`);
                    }}
                    className="h-8 px-2 text-xs text-secondary-500 hover:text-secondary-700 hover:bg-secondary-50"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          </div>
        ) : (
          <>
            {/* Filter Loading Indicator */}
            {loading && isFiltering && (
              <div className="flex items-center justify-center py-4 mb-6">
                <div className="flex items-center space-x-2 text-secondary-600">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="text-sm">Đang áp dụng bộ lọc...</span>
                </div>
              </div>
            )}

            <InfiniteScroll
              style={{
                height: "unset",
                overflow: "unset",
              }}
              dataLength={invitationRequests.length}
              next={() => {
                setPage((prev) => prev + 1);
                fetchMyGroupsInfiniteScroll(page + 1);
              }}
              hasMore={isLoadMore}
              loader={
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                </div>
              }
            >
              {invitationRequests.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="max-w-md">
                    <h3 className="text-xl font-semibold text-secondary-900 mt-6 mb-3">
                      {isFiltering
                        ? "Không tìm thấy lời mời nào"
                        : "Chưa có lời mời tham gia"}
                    </h3>
                    <p className="text-secondary-600 mb-8 leading-relaxed">
                      {isFiltering
                        ? "Thử điều chỉnh bộ lọc để tìm thấy lời mời phù hợp"
                        : "Bạn chưa có lời mời tham gia nhóm nào. Các lời mời mới sẽ xuất hiện tại đây."}
                    </p>

                    {/* Show clear filter button in empty state when filtering */}
                    {isFiltering && (
                      <div className="flex justify-center mt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setRootGroupId(undefined);
                            setSort(null);
                            setPage(1);
                            navigate(`${window.location.pathname}`);
                          }}
                          className="text-primary-600 border-primary-600 hover:bg-primary-50"
                        >
                          Xóa tất cả bộ lọc
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {invitationRequests.map((group) => (
                    <GroupCardInvite
                      key={group.groupId}
                      group={group}
                    />
                  ))}
                </div>
              )}
            </InfiniteScroll>
          </>
        )}
      </div>
    </div>
  );
};

export default GroupInviteScreen;
